import { graphql, PageProps, useStaticQuery } from 'gatsby';
import type { RenderRichTextData } from 'gatsby-source-contentful/rich-text';
import { intersection, orderBy, uniq } from 'lodash-es';
import React, { useEffect } from 'react';

import {
  Box, Container, FormControl, FormControlLabel, Radio, RadioGroup, Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2'; // Grid version 2
import { useTheme } from '@mui/material/styles';
import { useSeekaConverge } from '@seeka-labs/converge-react';
import { AnalyticsPageType } from '@shopify/hydrogen-react';

import SeoHead from '../components/head';
import { HorizontalSlantContainer } from '../components/horizontal-slant-container';
import { ContentfulAsset } from '../components/ui';
import { useAnalytics } from '../shared/analytics';
import { headerSlantContainerNoContent } from '../shared/styles/headerSlantContainer';
import {
  ExternalReferenceInfoItemCard, getColorForExternalReferenceInfoTag
} from '../templates/externalInfoReference/card';

export enum ExternalReferenceInfoItemType {
  Article = "Article",
  Video = "Video",
  Study = "Study"
}

export interface ExternalReferenceInfoItem {
  id: string
  sourceName: string
  externalLink: string
  contentful_id: string
  excerpt?: RenderRichTextData<null>
  tags: readonly string[]
  logo: ContentfulAsset
  referenceType: ExternalReferenceInfoItemType
}

export default function Decide() {
  const theme = useTheme();
  const headerSlantContainerNoContentStyles = headerSlantContainerNoContent();

  const analytics = useAnalytics();
  useEffect(() => {
    analytics.trackPageView(AnalyticsPageType.page);
  }, [])
  const converge = useSeekaConverge();

  const [filteredInfoItems, setFilteredInfoItems] = React.useState<Queries.DecidePageQuery['allExternalInfoReference']['nodes']>([])
  const query: Queries.DecidePageQuery = useStaticQuery(graphql`
    query DecidePage {
      allExternalInfoReference: allContentfulExternalInfoReference {
        nodes {
          id
          sourceName
          tags
          excerpt {
            raw
          }
          externalLink
          contentful_id
          referenceType
          __typename
          logo {
            id
            contentful_id
            __typename
            url
            title
            
            localFile {
              childImageSharp { 
                  gatsbyImageData(placeholder: NONE, height: 60, layout: CONSTRAINED)
              }
            }
          }
        }
      }
    }
  `)

  const allTags = orderBy(
    uniq(
      query.allExternalInfoReference.nodes.flatMap((e) => e.tags),
    ).filter((e) => e),
    [(e) => e],
    ["asc"],
  )

  const [selectedTag, setSelectedTag] = React.useState<string>("")

  React.useEffect(() => {
    setFilteredInfoItems(
      query.allExternalInfoReference.nodes.filter((info) => {
        return (
          selectedTag === "" ||
          intersection([selectedTag], info.tags || []).length > 0
        )
      })
        .sort((a, b) => (b.referenceType === "Video" ? 1 : -1))
    )
  }, [selectedTag])

  const gap = { xs: 2, sm: 2.5, md: 3, lg: 4, xl: 4 }

  const onTagSelect = (tag: string) => {
    setSelectedTag(tag)

    converge.track.viewContentItem({
      categoryName: 'topic',
      items: [{
        categoryName: 'topic.explicit',
        productIdentifier: tag,
        productName: tag
      }]
    })

    const clar = (window as any).clarity;
    if (clar) {
      clar("set", "topic.explicit", [tag])
    }

    const klav = (window as any).klaviyo;
    klav?.track('topic.explicit', {
      'topics': tag
    });
  }

  const tagSelector = (
    <FormControl sx={{ display: { xs: "none", sm: "inherit" } }}>
      {/* <FormLabel id="demo-controlled-radio-buttons-group">Gender</FormLabel> */}
      <RadioGroup
        aria-labelledby="demo-controlled-radio-buttons-group"
        name="controlled-radio-buttons-group"
        value={selectedTag}
        onChange={(event) => onTagSelect((event.target as HTMLInputElement).value)}
      >
        <FormControlLabel value={""} control={<Radio />} label={"All"} />
        {allTags.map((tag: string) => {
          const color = getColorForExternalReferenceInfoTag(tag);
          return (
            (<FormControlLabel
              key={tag}
              value={tag}
              control={<Radio />}
              label={<Typography sx={[color === 'default' ? {
                color: null
              } : {
                color: theme.palette[color].main
              }]}>{tag}</Typography>}
            />)
          );
        })}
      </RadioGroup>
    </FormControl>
  )

  return (<>
    <HorizontalSlantContainer id={"decide"} autoHeight>
      <Container sx={headerSlantContainerNoContentStyles}></Container>
    </HorizontalSlantContainer>
    <Box sx={{ minHeight: "100vh" }}>

      <Container maxWidth="xl">
        <Box paddingY={4}>
          {/* SEO */}
          <Typography variant="h1" gutterBottom sx={{ display: 'none' }}>
            Learn
          </Typography>

          <Grid container spacing={gap}>
            <Grid
              id="infos-search"
              pl={0}
              size={{
                xs: 12,
                sm: 4,
                md: 3,
                lg: 3,
                xl: 2
              }}>
              {tagSelector}
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 8,
                md: 9,
                lg: 9,
                xl: 10
              }}>
              <Grid container spacing={3}>
                {filteredInfoItems.map((info) => (
                  <ExternalReferenceInfoItemCard key={info.id} onTagSelect={onTagSelect} {...info} referenceType={info.referenceType as ExternalReferenceInfoItemType} />
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>
        {/* <Box paddingY={4}>
        <Typography variant="h2" gutterBottom>
          Frameworks
        </Typography>
        <Grid container spacing={4}>
          {infos.map((info) => (
            <InfoItemCard key={info.id} {...info} />
          ))}
        </Grid>
      </Box> */}
      </Container>
    </Box>
  </>);
}

export const Head = (props: PageProps) => {
  return (
    <SeoHead
      title="Learn about Methylene Blue"
      pathname={props.location.pathname}
      description="Research and Studies on Methylene Blue"
    />
  )
}